import { useMemo } from 'react'
import { TFunction, useTranslation } from 'react-i18next'

import { Invitation, Message, MessageType } from 'lib/_types'
import dayjs from 'dayjs'

type Props = {
  message: Message
  isCurrentUserMessage: boolean
}

const ALLOWED_TYPE: Readonly<MessageType[]> = [
  'owner_kick',
  'user_joined',
  'user_leave',
  'screenshot_warning',
]

export const ChatMessageNotify = ({ message, isCurrentUserMessage }: Props) => {
  if (!ALLOWED_TYPE.includes(message?.message_type)) return null

  const { t } = useTranslation()

  const text = useMemo(() => {
    switch (message.message_type) {
      case 'screenshot_warning':
        return isCurrentUserMessage
          ? t('chat.message.screenshot.you')
          : t('chat.message.screenshot.other')

      case 'owner_kick':
        return getOwnerKickText(t, message.text)

      case 'user_joined':
        return getUserJoinedText(t, message.text, message.invitation)

      case 'user_leave':
        return getUserLeaveText(t, message.text)

      default:
        return message.text
    }
  }, [message.text, message.message_type])

  return (
    <div className="Messages__item__notify">
      <div className="Messages__item__notify__div">
        <div>{dayjs(message.created_at * 1000).format('HH:mm')}</div>
        <div>{text}</div>
      </div>
    </div>
  )
}

const getOwnerKickText = (t: TFunction, text: string = '') => {
  const matched = extractText(
    [
      // kicked many
      /✓ (.+)が(.*)と他(.+)人を退室させました/,
      /✓ (.+) removed (.+) and other (.+) from group/,
      // kicked one
      /✓ (.+)が(.+)を退室させました/,
      /✓ (.+) removed (.+) from group/,
    ],
    text
  )

  if (!matched) return text

  const [owner, name, otherCount] = matched

  return matched.length === 2
    ? t('chat.message.kicked.one', { owner, name })
    : t('chat.message.kicked.many', { owner, name, otherCount })
}

const getUserJoinedText = (
  t: TFunction,
  text: string = '',
  invitation: Invitation
) => {
  const matched = extractText(
    [
      // joined many
      /✓ (.+)さんと(.+)名が参加しました.*/,
      /✓ (.+) and (.+) others joined the chat.*/,
      // joined one
      /✓ (.+)さんが参加しました.*/,
      /✓ (.+) joined the chat.*/,
    ],
    text
  )

  if (!matched) return text

  const [name, otherCount] = matched

  if (invitation) {
    return matched.length === 1
      ? t('chat.message.invited.one', {
          inviter: invitation.inviter,
          invited: invitation.first_user,
        })
      : t('chat.message.invited.many', {
          inviter: invitation.inviter,
          invited: invitation.first_user,
          otherCount: invitation.users_count - 1,
        })
  }

  return matched.length === 1
    ? t('chat.message.joined.one', { name })
    : t('chat.message.joined.many', { name, otherCount })
}

const getUserLeaveText = (t: TFunction, text: string = '') => {
  const matched = extractText(
    [
      // left one, there are no left many case
      /✓ (.+)さんが退室しました/,
      /✓ (.+) left/,
    ],
    text
  )

  if (!matched) return text

  return t('chat.message.left', { name: matched[0] })
}

// get params from 1st matched regex given
// and omit the blank string from result
// please sorted regex by priority before call this function
const extractText = (regex: RegExp[], text: string) =>
  regex
    .map((x) => text.split(x))
    .filter((x) => x.length > 1)[0]
    ?.filter((x) => x.length)
